import {
  AGGREGATE_API,
  ASURA_ENDPOINT,
  ASURA_HEADERS,
  BUILD_MODE,
  MISO_WEB_URL,
  PUBLIC_STRAPI_CLIENT_API,
  STRAPI_API,
  STRAPI_CLIENT_API,
} from '@utils/constants';

import { FeedbackResponse } from '@customTypes/Review';

type Response = any;

const getHeaders = (token: string) => {
  return {
    'Accept': 'application/json',
    'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
    'x-user-agent': 'Miso-Partner/2.10.0 (web)',
    'Authorization': `Bearer ${token}`,
  };
};

export type OnBoardingUpdateRequest = {
  supplier_id?: number;
  name?: string;
  service_ids?: number[];
  paths?: string[];
  sources?: string[];
  coverages?: number[];
  skills?: string[];
  gender?: string;
  birth_year?: number;
  visa?: boolean;
  nationality?: string;
  zoophobia?: boolean;
  address?: {
    region: string;
    rest: string;
  };
  referrer?: {
    code: string;
    id: number;
  };
};

export type OnBoardingUpdateResponse = {
  [key: string]: OnBoardingStatus;
};

export enum OnBoardingStatus {
  lead = 'Lead',
  qualifying = 'Qualifying',
  qualified = 'Qualified',
  onBoarded = 'Onboarded',
  Disqualified = 'Disqualified',
  Unknown = 'Unknown',
  inactive = 'Inactive',
}

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE';
type Headers = { [key: string]: string };
type QueryParams = { [key: string]: string | number | boolean };

export const fetchRequest = async (
  url: string,
  method: HttpMethod,
  headers: Headers,
  body?: string,
  queryParams?: QueryParams
): Promise<Response> => {
  const urlWithQs = queryParams ? addQueryStrings(url, queryParams) : url;
  const requestInit: RequestInit = {
    method,
    headers,
    body,
  };
  const response: Response = await fetch(urlWithQs, requestInit);

  // 여기서 validateResponse를 호출하거나 다른 검증 로직을 수행할 수 있습니다.
  // validateResponse(response);
  return response;
};

const addQueryStrings = (url: string, queryParams: QueryParams): string => {
  const params = new URLSearchParams();
  Object.keys(queryParams).forEach((key) => params.append(key, queryParams[key].toString()));
  return `${url}?${params.toString()}`;
};

export const fetchOnboardingSupplierUpdate = async ({
  request,
  token,
}: {
  request: OnBoardingUpdateRequest;
  token: string;
}): Promise<OnBoardingUpdateResponse> => {
  const env = BUILD_MODE === 'production' ? 'v1' : 'staging';
  const url = `${MISO_WEB_URL}/lambdaro/supplier/supplier-${env}-update`;
  const headers = getHeaders(token);
  const response = (await fetchRequest(url, 'POST', headers, JSON.stringify(request))) as Response;
  return (await response.json()) as OnBoardingUpdateResponse;
};

const generateNestedQueryString = (queryObj: Record<string, any>, prefix?: string): string => {
  const pairs: string[] = [];

  for (const [key, value] of Object.entries(queryObj)) {
    const newPrefix = prefix ? `${prefix}[${key}]` : key;

    if (typeof value === 'object' && !Array.isArray(value)) {
      pairs.push(generateNestedQueryString(value, newPrefix));
    } else if (Array.isArray(value)) {
      value.forEach((item) => {
        if (typeof item === 'object') {
          pairs.push(generateNestedQueryString(item, `${newPrefix}[]`));
        } else {
          pairs.push(`${newPrefix}[]=${encodeURIComponent(item)}`);
        }
      });
    } else {
      const numericValue = parseInt(value, 10);
      if (!isNaN(numericValue) && newPrefix.endsWith('[id][$eq]')) {
        pairs.push(`${newPrefix}=${numericValue}`);
      } else {
        pairs.push(`${newPrefix}=${encodeURIComponent(value)}`);
      }
    }
  }

  return pairs.join('&');
};

export const fetchStrapi = async (
  apiId: string,
  documentId?: string | number,
  query?: Record<string, any>,
  isPopulateDeep = true
): Promise<Response> => {
  try {
    const queryString = query ? generateNestedQueryString(query) : '';
    const url = `${STRAPI_API}/api/${apiId}${documentId ? `/${documentId}` : ''}?${
      isPopulateDeep ? 'populate=deep&' : ''
    }${queryString}`;

    const resp = await fetch(url, {});

    const json = await resp.json();
    return json;
  } catch (e) {
    throw Error('Error fetching from Strapi API');
  }
};

export const fetchAsuraMiddleware = async (
  path: string,
  body: Record<string, any>,
  clientside: boolean = false
): Promise<Response> => {
  try {
    const resp = await fetch(`${clientside ? PUBLIC_STRAPI_CLIENT_API : STRAPI_CLIENT_API}/${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const json = await resp.json();
    return json;
  } catch (e) {
    throw Error('Error fetching from Next.js API');
  }
};

export const fetchAggregate = async (templateId: string, serviceId?: string): Promise<Response> => {
  try {
    const resp = await fetch(`${AGGREGATE_API}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ template_id: templateId, service_id: serviceId }),
    });
    const json = await resp.json();
    return json;
  } catch (e) {
    throw Error(`Error fetching from Next.js API: templateId: ${templateId}, serviceId: ${serviceId}`);
  }
};

export const fetchAggregatePartner = async (partnerId: number, serviceId?: number): Promise<Response> => {
  try {
    const resp = await fetch(`${AGGREGATE_API}Partner`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ partnerId, serviceId }),
    });
    const json = await resp.json();
    return json;
  } catch (e) {
    throw Error(`Error fetching from Next.js API: partnerId: ${partnerId}`);
  }
};

export const fetchFeedbackSummary = async (partnerId: number): Promise<Response> => {
  try {
    const resp = await fetch(`${MISO_WEB_URL}/review/public/receivers/customer/${partnerId}/feedback-summary`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    const json = await resp.json();
    return json;
  } catch (e) {
    throw Error(`Error fetching from Next.js API feedback-summary: partnerId: ${partnerId}`);
  }
};

export const fetchFeedbacks = async (partnerId: number, queryString: string): Promise<FeedbackResponse> => {
  try {
    const resp = await fetch(`${MISO_WEB_URL}/review/public/receivers/customer/${partnerId}/feedbacks?${queryString}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    const json = await resp.json();
    return json;
  } catch (e) {
    throw Error(`Error fetching from Next.js API feedbacks: partnerId: ${partnerId}`);
  }
};

export const fetchAggregatePartnerDirectory = async (serviceCode: string): Promise<Response> => {
  try {
    const resp = await fetch(`${AGGREGATE_API}PartnerDirectory`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ serviceCode }),
    });
    const json = await resp.json();
    return json;
  } catch (e) {
    throw Error(`Error fetching from Next.js API: serviceId: ${serviceCode}`);
  }
};

export const fetchAsura = async (query: string, variables: any | null = null): Promise<Response> => {
  try {
    const headers = {
      'Content-Type': 'application/json',
      'x-asura-project-name': 'strapi',
      'x-asura-datasource-name': 'rfq',
    };
    if (ASURA_HEADERS) {
      Object.assign(headers, JSON.parse(Buffer.from(ASURA_HEADERS, 'base64').toString('utf-8')));
    }
    const body = JSON.stringify({ query, variables });
    const response = await fetch(ASURA_ENDPOINT, { method: 'POST', headers, body });
    if (process.env.ASURA_DEBUG) {
      console.debug('ASURA_DEBUG:', { asura: ASURA_ENDPOINT, query, variables, response });
    }
    if (response.ok) {
      const json = await response.json();
      const { errors } = json;
      if (!errors) {
        return json;
      } else {
        throw Error((errors[0] && errors[0].message) || 'graphql error');
      }
    } else {
      throw Error(`Error fetching from asura: ${JSON.stringify(response)}`);
    }
  } catch (e) {
    console.error(e);
    throw Error(e as string);
  }
};
